/**
 * App-specific HTML builders + openPrint* entry points for slab, foundation, console, composite.
 */

import { eurocodeToHtmlSubscripts } from '../utils/eurocodeNotation';
import { slabVerificationNotes, slabVerificationRows } from '../components/slab_calculator/verificationDisplay';
import {
  buildPrintDocumentHtml,
  openPrintHtmlDocument,
  printSectionHtml,
  escapeHtml,
} from '../utils/printReportShared';

function fmtNum(v, d = 2) {
  if (v == null) return '—';
  return Number(v).toLocaleString('en-US', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  }).replace(/,/g, ' ');
}

function imgSection(title, base64, alt) {
  if (!base64) return '';
  const src = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
  return printSectionHtml(title, `<div class="chart-wrap"><img src="${src}" alt="${escapeHtml(alt)}" /></div>`);
}

/** --- Slab --- */
export function buildSlabPrintHtml(results, { generatedAt, inputsChangedHint } = {}) {
  if (!results) return buildPrintDocumentHtml({ title: 'Slab analysis', bodyHtml: '<p>No results.</p>' });
  const def = results.deflections || {};
  const mom = results.moments || {};
  const lev = results.leveling_volumes || {};
  const ver = results.verifications || {};
  const images = results.images || {};
  const contactDiag = results.contact_diagnostics;
  const wqComposite = results.wq_composite;
  const steel = results.steel_composite;
  const ts = results.tendon_strain;

  const sub = [
    generatedAt || new Date().toLocaleString(),
    inputsChangedHint ? 'Note: inputs may have changed since analysis.' : '',
  ]
    .filter(Boolean)
    .join(' · ');

  let body = '';

  if (steel) {
    const warn = (steel.warnings || []).map((w) => `<li>${escapeHtml(w)}</li>`).join('');
    body += printSectionHtml(
      'Steel-composite slab',
      `<table class="report kv-table"><tbody>
        <tr><td>Sheet</td><td>${escapeHtml(steel.sheet_id || '')} ${fmtNum(steel.thickness_mm, 1)} mm</td></tr>
        <tr><td>Construction sag</td><td class="num">${fmtNum(steel.construction_sag_mm)} mm</td></tr>
        <tr><td>M_Ed / M_Rd</td><td class="num">${fmtNum(steel.M_Ed_kNm_per_m)} / ${fmtNum(steel.M_Rd_kNm_per_m)} kNm/m</td></tr>
        <tr><td>Degree of shear connection η</td><td class="num">${fmtNum(steel.degree_of_shear_connection, 2)}</td></tr>
        <tr><td>Sagging check</td><td>${steel.ok ? 'PASS' : 'FAIL'}</td></tr>
      </tbody></table>${warn ? `<ul>${warn}</ul>` : ''}`,
    );
  }

  body += printSectionHtml(
    'Deflections (mm)',
    `<table class="report kv-table"><tbody>
      <tr><td>${steel ? 'Stage 1 sag (sheet + wet concrete)' : 'Stage 1 sag (precast + wet topping)'}</td><td class="num">${fmtNum(def.stage1_wet_topping_sag_max_mm ?? def.stage1_max_mm)}</td></tr>
      ${steel ? '' : `<tr><td>Precamber (initial P, upward)</td><td class="num">${fmtNum(def.precamber_initial_up_mm)}</td></tr>`}
      <tr><td>Stage 1 net minimum</td><td class="num">${fmtNum(def.stage1_net_min_mm)}</td></tr>
      <tr><td>Stage 2 (composite perm)</td><td class="num">${fmtNum(def.stage2_max_mm)}</td></tr>
      <tr><td>Dead load max</td><td class="num">${fmtNum(def.dead_load_max_mm)}</td></tr>
      <tr><td>Live load max</td><td class="num">${fmtNum(def.live_load_max_mm)}</td></tr>
    </tbody></table>`,
  );

  body += printSectionHtml(
    'ULS moments (kNm/m)',
    `<table class="report kv-table"><tbody>
      <tr><td>Mxx min / max</td><td class="num">${fmtNum(mom.Mxx_min_ULS)} / ${fmtNum(mom.Mxx_max_ULS)}</td></tr>
      <tr><td>Myy min / max</td><td class="num">${fmtNum(mom.Myy_min_ULS)} / ${fmtNum(mom.Myy_max_ULS)}</td></tr>
    </tbody></table>`,
  );

  body += printSectionHtml(
    'Leveling volumes (m³)',
    `<table class="report kv-table"><tbody>
      <tr><td>Stage 1</td><td class="num">${fmtNum(lev.stage1_m3, 3)}</td></tr>
      <tr><td>Stage 3</td><td class="num">${fmtNum(lev.stage3_m3, 3)}</td></tr>
      <tr><td>Total</td><td class="num"><strong>${fmtNum(lev.total_m3, 3)}</strong></td></tr>
    </tbody></table>`,
  );

  if (ts) {
    body += printSectionHtml(
      'Tendon strain (‰, linear elastic)',
      `<table class="report kv-table"><tbody>
        <tr><td>Min / Max</td><td class="num">${fmtNum(ts.min_permille)} / ${fmtNum(ts.max_permille)}</td></tr>
        <tr><td>Stress-limit equivalent</td><td class="num">${fmtNum(ts.limit_equivalent_permille ?? ts.limit_permille)}</td></tr>
        <tr><td>Service check</td><td>${ts.ok === false ? 'EXCEEDED' : ts.ok === true ? 'OK' : '—'}</td></tr>
      </tbody></table>`,
    );
  }
  const tendonStress = results.tendon_stress;
  if (tendonStress) {
    body += printSectionHtml(
      'Tendon stress (MPa)',
      `<table class="report kv-table"><tbody>
        <tr><td>Range</td><td class="num">${fmtNum(tendonStress.min_mpa, 0)} – ${fmtNum(tendonStress.max_mpa, 0)}</td></tr>
        <tr><td>Transfer (initial)</td><td class="num">${fmtNum(tendonStress.transfer_mpa, 0)}</td></tr>
        <tr><td>Nominal Sn</td><td class="num">${fmtNum(tendonStress.strand_area_mm2, 1)} mm²${tendonStress.tendon_preset ? ` (${escapeHtml(tendonStress.tendon_preset)})` : ''}</td></tr>
      </tbody></table>`,
    );
  }

  if (wqComposite?.warnings?.length || wqComposite?.beams?.length) {
    const warn = (wqComposite.warnings || []).map((w) => `<li>${escapeHtml(w)}</li>`).join('');
    const rows = (wqComposite.beams || [])
      .map(
        (b) =>
          `<tr><td>${escapeHtml(b.label || '')}</td><td class="num">${fmtNum(b.M_Ed_kNm)} / ${fmtNum(b.M_Rd_kNm)}</td><td class="num">${fmtNum(b.V_Ed_kN)} / ${fmtNum(b.V_Rd_kN)}</td><td class="num">${fmtNum(b.connector_as_required_mm2_per_m, 0)} / ${fmtNum(b.connector_as_provided_mm2_per_m, 0)}</td><td>${escapeHtml(b.governing_check || '')}</td><td>${b.ok ? 'PASS' : 'FAIL'}</td></tr>`,
      )
      .join('');
    body += printSectionHtml(
      'WQ composite beams',
      `${warn ? `<ul>${warn}</ul>` : ''}${
        rows
          ? `<table class="report allow-row-split"><thead><tr><th>Line</th><th>M_Ed / M_Rd (kNm)</th><th>V_Ed / V_Rd (kN)</th><th>As,req / As,prov (mm²/m)</th><th>Governing</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>`
          : ''
      }`,
      { allowBreak: true },
    );
  }

  if (contactDiag) {
    body += printSectionHtml(
      'Slab–beam contact diagnostics',
      `<table class="report kv-table"><tbody>
        <tr><td>Converged</td><td>${contactDiag.converged ? 'Yes' : 'No'}${contactDiag.iterations != null ? ` (${contactDiag.iterations} iter.)` : ''}</td></tr>
        <tr><td>Mesh</td><td class="num">${contactDiag.mesh_nx ?? '—'} × ${contactDiag.mesh_ny ?? '—'}</td></tr>
        <tr><td>Equilibrium error</td><td class="num">${fmtNum(contactDiag.equilibrium_error_kN, 3)} kN</td></tr>
        <tr><td>Total bearing / target</td><td class="num">${fmtNum(contactDiag.total_bearing_kN, 1)} / ${fmtNum(contactDiag.target_upward_reaction_kN, 1)} kN</td></tr>
      </tbody></table>`,
    );
  }

  const verRows = slabVerificationRows(ver)
    .map(
      (row) =>
        `<tr><td>${eurocodeToHtmlSubscripts(row.label)}</td><td>${row.ok ? 'PASS' : 'FAIL'}</td><td>${eurocodeToHtmlSubscripts(row.message)}</td></tr>`,
    )
    .join('');
  const verNotes = slabVerificationNotes(ver)
    .map((note) => `<p>${eurocodeToHtmlSubscripts(note)}</p>`)
    .join('');
  body += printSectionHtml(
    steel ? 'Verifications' : 'EN 1992 verifications',
    `${verNotes}<table class="report allow-row-split"><thead><tr><th>Check</th><th>Status</th><th>Message</th></tr></thead><tbody>${verRows}</tbody></table>`,
    { allowBreak: true },
  );

  body += imgSection('Slab results (deflection & moments)', images.slab_results, 'Slab results');
  body += imgSection('Reactions (ULS)', images.reactions, 'Reactions');
  body += imgSection(
    images.nonlinear_bearing ? 'Bearing force & interface kinematics' : 'Bearing force',
    images.nonlinear_bearing || images.bearing_force,
    'Bearing',
  );
  body += imgSection('Beam deflection', images.beam_deflection, 'Beam deflection');
  body += imgSection('Rebar 2-way', images.rebar_2d, 'Rebar');
  body += imgSection('Tendons 1-way', images.tendon_2d, 'Tendons');

  return buildPrintDocumentHtml({
    title: 'Slab calculator — analysis report',
    subtitle: sub,
    bodyHtml: body,
  });
}

export function openSlabPrintReport(results, options) {
  openPrintHtmlDocument(buildSlabPrintHtml(results, options));
}

function kvTable(rows) {
  const body = rows
    .filter(Boolean)
    .map(([label, value]) => `<tr><td>${eurocodeToHtmlSubscripts(label)}</td><td>${value}</td></tr>`)
    .join('');
  return `<table class="report kv-table"><tbody>${body}</tbody></table>`;
}

function ratioPercent(load, capacity) {
  const applied = Number(load) || 0;
  const resistance = Number(capacity) || 0;
  if (resistance > 0) return `${((applied / resistance) * 100).toFixed(1)}%`;
  return applied > 0 ? '100.0%' : '0.0%';
}

function foundationFixityText(input) {
  const mode = input?.pile_head_fixity_mode || 'override';
  const value = Number(input?.pile_head_fixity ?? 0);
  const preset = input?.pile_head_fixity_preset;
  const computed = input?.pile_head_fixity_computed || {};
  if (mode === 'envelope') {
    return 'Envelope of pinned (0.00) and fixed (1.00); governing bound varies by metric';
  }
  if (mode === 'preset') {
    return `${value.toFixed(2)} (${preset ? `${preset} preset` : 'preset'})`;
  }
  if (mode === 'computed') {
    return `${value.toFixed(2)} (computed: embed ${fmtNum(computed.cap_embedment_m || 0, 2)} m, ${computed.top_rebar_development || 'none'} rebar, cap ${fmtNum(computed.cap_thickness_m || 0, 2)} m)`;
  }
  return `${value.toFixed(2)} (advanced override)`;
}

function equilibriumStatusLabel(status) {
  if (status === 'converged') return 'Converged';
  if (status === 'failed_convergence') return 'Failed to converge';
  if (status === 'unstable_liftoff') return 'Unstable (pile liftoff)';
  if (status === 'unstable_singular') return 'Unstable (singular matrix)';
  return status ? `Status: ${status}` : '—';
}

/** --- Foundation --- */
export function buildFoundationPrintHtml(results, {
  chartImageDataUrl,
  momentChartImageDataUrl,
  diagrams = [],
  generatedAt,
} = {}) {
  if (!results) {
    return buildPrintDocumentHtml({ title: 'Foundation analysis', bodyHtml: '<p>No results.</p>' });
  }
  const {
    input_parameters: input = {},
    capacities = {},
    stiffnesses = {},
    utilization = {},
    safety_check: safetyCheck,
    equilibrium,
    stability_verifications: stab = {},
    seismic_breakdown: seismic,
    seismic_envelope: seismicEnvelope,
    distributed_check: distributed,
    fixity_envelope: fixityEnvelope,
    warnings,
    design_basis: designBasis,
  } = results;
  const loads = input.loads || {};
  const sub = `${generatedAt || new Date().toLocaleString()} · ${safetyCheck ? 'Design PASS' : 'Design FAIL'}`;

  let body = '';
  if (Array.isArray(warnings) && warnings.length) {
    body += printSectionHtml(
      'Warnings',
      `<ul>${warnings.map((w) => `<li>${escapeHtml(w)}</li>`).join('')}</ul>`,
    );
  }

  if (designBasis) {
    body += printSectionHtml(
      'Design basis (EC7 DA2 — Finnish NA)',
      kvTable([
        ['Framework', escapeHtml(designBasis.code_editions || '—')],
        ['Design approach', escapeHtml(designBasis.design_approach || '—')],
        ['Bearing γ_Rv', escapeHtml(String(designBasis.gamma_Rv_bearing ?? '—'))],
        ['Pile γ_b / γ_s', `${escapeHtml(String(designBasis.gamma_b ?? '—'))} / ${escapeHtml(String(designBasis.gamma_s ?? '—'))}`],
        ['ξ (base / shaft)', `${escapeHtml(String(designBasis.xi_base ?? '—'))} / ${escapeHtml(String(designBasis.xi_shaft ?? '—'))}`],
        designBasis.q_ult_primary_kPa != null
          ? ['q_ult (primary layer)', `${fmtNum(designBasis.q_ult_primary_kPa, 0)} kPa`]
          : null,
        designBasis.note ? ['Note', eurocodeToHtmlSubscripts(designBasis.note)] : null,
      ]),
    );
  }

  if (seismic) {
    const seismicRows = [];
    if (seismic.base_shear) {
      const bs = seismic.base_shear;
      seismicRows.push(['Base shear F_b', `${fmtNum(bs.F_b_kN, 1)} kN`]);
      seismicRows.push(['T_1', `${fmtNum(bs.T1_s, 2)} s`]);
      seismicRows.push(['S_d(T_1)', `${fmtNum(bs.Sd_T1_ms2, 3)} m/s²`]);
      seismicRows.push(['Ground type / q', `${escapeHtml(bs.ground_type || '—')} / ${fmtNum(bs.q, 2)}`]);
    }
    if (seismic.capacity_design_factor != null) {
      seismicRows.push(['γ_Rd·Ω', fmtNum(seismic.capacity_design_factor, 2)]);
    }
    if (seismic.liquefaction && !seismic.liquefaction.skipped) {
      seismicRows.push([
        'Liquefaction',
        seismic.liquefaction.any_liquefied
          ? 'One or more layers flagged as liquefied (shaft resistance reduced)'
          : 'No liquefied layers (FS ≥ 1.25 or not applicable)',
      ]);
    }
    if (seismic.kinematic) {
      seismicRows.push([
        'Kinematic bending',
        seismic.kinematic.skipped
          ? escapeHtml(seismic.kinematic.reason || 'Not triggered (EC8-5 §5.4.2 conditions).')
          : (seismic.kinematic.interfaces || [])
            .map((row) => `z = ${fmtNum(row.depth_m, 1)} m: M_kin ≈ ${fmtNum(row.M_kin_kNm, 1)} kNm`)
            .join('; ') || '—',
      ]);
    }
    if (seismic.mononobe_okabe?.details) {
      seismicRows.push([
        'Mononobe–Okabe ΔP_AE',
        `${fmtNum(seismic.mononobe_okabe.Delta_P_AE_kN, 1)} kN · K_AE = ${fmtNum(seismic.mononobe_okabe.details.K_AE, 3)}`,
      ]);
    }
    if (seismicEnvelope) {
      seismicRows.push([
        'Directional envelope',
        `Governing combo ${seismicEnvelope.governing_combo_index + 1} of ${seismicEnvelope.combos_evaluated} (±30% rule)`,
      ]);
    }
    if (seismicRows.length) {
      body += printSectionHtml('Eurocode 8 — seismic', kvTable(seismicRows));
    }
  }

  if (distributed?.error) {
    body += printSectionHtml(
      'Distributed pile check',
      `<p>${escapeHtml(String(distributed.error))}</p>`,
    );
  } else if (distributed?.z_m) {
    let distributedHtml = kvTable([
      ['Max N–M utilization along shaft', `${((distributed.max_utilization || 0) * 100).toFixed(1)}%`],
      ['Critical depth', `${fmtNum(distributed.critical_depth_m, 2)} m`],
      ['Head-level max UR', `${((utilization.max_utilization || 0) * 100).toFixed(1)}%`],
    ]);
    if (momentChartImageDataUrl) {
      const src = momentChartImageDataUrl.startsWith('data:')
        ? momentChartImageDataUrl
        : `data:image/png;base64,${momentChartImageDataUrl}`;
      distributedHtml += `<div class="chart-wrap"><img src="${src}" alt="Bending moment vs depth" /></div>`;
    }
    if (input.pile_head_fixity_mode === 'envelope' && fixityEnvelope?.governing_per_metric) {
      const bounds = Object.entries(fixityEnvelope.governing_per_metric)
        .slice(0, 8)
        .map(([key, value]) => `${escapeHtml(key)}: fixity=${value === 1 ? '1.0' : '0.0'}`)
        .join(' | ');
      distributedHtml += `<p class="muted">Governing bound by metric: ${bounds}</p>`;
    }
    body += printSectionHtml('Distributed pile check', distributedHtml, { allowBreak: true });
  }

  const tension = stab.tension || {};
  const sliding = stab.sliding || {};
  const lateralX = stab.lateral_x || {};
  const lateralY = stab.lateral_y || {};
  const lateralLoad = Math.max(Number(lateralX.load) || 0, Number(lateralY.load) || 0);
  const lateralCapacity = Math.min(
    Number(lateralX.capacity) || 0,
    Number(lateralY.capacity) || 0,
  );
  const lateralUr = Math.max(Number(utilization.lateral_x) || 0, Number(utilization.lateral_y) || 0);
  const complianceRows = [
    [
      'ULS compression (§6.5.2)',
      `${fmtNum(Math.abs(loads.N || 0), 0)} kN`,
      `${fmtNum(capacities.compression || 0, 0)} kN`,
      `${((Number(utilization.compression) || 0) * 100).toFixed(1)}%`,
      (Number(utilization.compression) || 0) <= 1 ? 'PASS' : 'FAIL',
    ],
    [
      'ULS tension (§6.5.3)',
      `${fmtNum(tension.load || 0, 0)} kN`,
      `${fmtNum(tension.capacity || 0, 0)} kN`,
      ratioPercent(tension.load, tension.capacity),
      tension.status === 'PASS' ? 'PASS' : 'FAIL',
    ],
    [
      'Sliding (§6.5.3)',
      `${fmtNum(sliding.driving_force, 0)} kN`,
      `${fmtNum(sliding.resisting_force, 0)} kN`,
      ratioPercent(sliding.driving_force, sliding.resisting_force),
      sliding.status === 'PASS' ? 'PASS' : 'FAIL',
    ],
    [
      'Lateral load (§6.5.4)',
      `${fmtNum(lateralLoad, 0)} kN`,
      `${fmtNum(lateralCapacity, 0)} kN`,
      `${(lateralUr * 100).toFixed(1)}%`,
      lateralX.status === 'PASS' && lateralY.status === 'PASS' ? 'PASS' : 'FAIL',
    ],
  ].map((cells) => `<tr>${cells.map((cell, i) => `<td${i ? ' class="num"' : ''}>${cell}</td>`).join('')}</tr>`).join('');
  body += printSectionHtml(
    'Eurocode 7 design compliance',
    `<table class="report allow-row-split"><thead><tr><th>Check</th><th class="num">Action</th><th class="num">Resistance</th><th class="num">UR</th><th class="num">Status</th></tr></thead><tbody>${complianceRows}</tbody></table>`,
    { allowBreak: true },
  );

  const lengthNote = input.actual_physical_length && input.actual_physical_length !== input.pile_length
    ? ` (clipped to ${fmtNum(input.actual_physical_length, 2)} m)`
    : '';
  const soilText = (input.soil_layers || [])
    .map((layer) => `${escapeHtml(layer.name)} (${fmtNum(layer.thickness, 2)} m)`)
    .join(', ') || '—';
  body += printSectionHtml(
    'Model and loads',
    kvTable([
      ['Foundation', `${escapeHtml(input.foundation_type || '—')} · ${escapeHtml(String(input.num_piles ?? '—'))} piles · ${escapeHtml(input.pile_code || '—')}`],
      ['Length', `${fmtNum(input.pile_length, 2)} m${lengthNote}`],
      ['Head fixity', escapeHtml(foundationFixityText(input))],
      input.pile_head_fixity_audit ? ['Fixity audit', escapeHtml(input.pile_head_fixity_audit)] : null,
      ['Concrete', escapeHtml(input.concrete_grade || '—')],
      ['Soil layers', soilText],
      ['Vertical load', `${fmtNum(loads.N || 0, 0)} kN`],
      ['Cap self-weight', `${fmtNum(capacities.cap_weight || 0, 0)} kN`],
      ['Horizontal load', `Vx ${fmtNum(loads.Vx || 0, 0)} kN · Vy ${fmtNum(loads.Vy || 0, 0)} kN`],
      ['Moment', `Mx ${fmtNum(loads.Mx || 0, 0)} kNm · My ${fmtNum(loads.My || 0, 0)} kNm · Mz ${fmtNum(loads.Mz || loads.moment_z || 0, 0)} kNm`],
    ]),
  );

  const breakdownRows = [
    ['Soil bearing at yield', `${fmtNum(capacities.soil_bearing_yield || 0, 0)} kN`],
  ];
  if (input.foundation_type !== 'spread_footing') {
    breakdownRows.push(['Pile group compression', `${fmtNum(capacities.pile_compression_group || 0, 0)} kN`]);
  }
  if (stab.torsion) {
    breakdownRows.push(['Applied torsion Mz', `${fmtNum(stab.torsion.moment_z_kNm || 0, 1)} kNm`]);
    breakdownRows.push(['Rotation θz', `${fmtNum(stab.torsion.rotation_z_deg || 0, 4)}°`]);
  }
  if (input.foundation_type !== 'spread_footing' && capacities.pile_compression_group) {
    const singleGeo = capacities.single_pile_geotech
      ? capacities.single_pile_geotech
      : capacities.pile_compression_group / (input.num_piles || 5);
    const buckling = capacities.single_pile_buckling && capacities.single_pile_buckling < 999999
      ? `${fmtNum(capacities.single_pile_buckling, 0)} kN`
      : 'Not limiting (high capacity)';
    breakdownRows.push(['Single pile geotechnical capacity', `~${fmtNum(singleGeo, 0)} kN`]);
    breakdownRows.push(['Group efficiency', capacities.pile_group_efficiency ? fmtNum(capacities.pile_group_efficiency, 3) : 'N/A']);
    breakdownRows.push(['Buckling capacity', buckling]);
    breakdownRows.push(['Max pile utilization', `${((Number(utilization.max_pile_utilization) || 0) * 100).toFixed(1)}%`]);
  }
  breakdownRows.push(['Piles R_h,geotech', `${fmtNum(sliding.Rh_geotech || 0, 0)} kN`]);
  breakdownRows.push(['Cap side bearing R_passive', `${fmtNum(sliding.passive_pressure || 0, 0)} kN`]);
  breakdownRows.push(['Cap underside friction R_sliding', `${fmtNum(sliding.base_friction || 0, 0)} kN`]);
  let breakdownHtml = kvTable(breakdownRows);
  if (chartImageDataUrl) {
    const src = chartImageDataUrl.startsWith('data:') ? chartImageDataUrl : `data:image/png;base64,${chartImageDataUrl}`;
    breakdownHtml += `<div class="chart-wrap"><img src="${src}" alt="Design resistances" /></div>`;
  }
  body += printSectionHtml('Capacity breakdown', breakdownHtml, { allowBreak: true });

  (diagrams || []).forEach((diagram) => {
    if (!diagram?.html) return;
    body += printSectionHtml(
      diagram.title || 'Figure',
      `<div class="svg-wrap">${diagram.html}</div>`,
      { allowBreak: true },
    );
  });

  if (equilibrium && !equilibrium.error) {
    const maxS = Number(equilibrium.max_settlement_mm) || 0;
    const minS = Number(equilibrium.min_settlement_mm) || 0;
    body += printSectionHtml(
      'Settlement',
      kvTable([
        ['Max settlement', `${fmtNum(maxS, 2)} mm`],
        ['Min settlement', `${fmtNum(minS, 2)} mm`],
        ['Average settlement', `${fmtNum(equilibrium.avg_settlement_mm, 2)} mm`],
        ['Differential settlement', `${fmtNum(maxS - minS, 2)} mm`],
        ['Equilibrium', escapeHtml(equilibriumStatusLabel(equilibrium.status))],
      ]),
    );
  } else if (equilibrium?.error) {
    body += printSectionHtml('Equilibrium', `<p>${escapeHtml(String(equilibrium.error))}</p>`);
  }

  body += printSectionHtml(
    'Stiffness',
    kvTable([
      ['Soil stiffness k_geo', `${fmtNum(stiffnesses.k_geo || 0, 0)} kN/m`],
      ['Shaft friction k_s', `${fmtNum(stiffnesses.k_shaft || 0, 0)} kN/m per pile`],
      ['Tip elastic k_b', `${fmtNum(stiffnesses.k_tip || 0, 0)} kN/m per pile`],
      ['Pile structural EA/L', `${fmtNum(stiffnesses.k_pile_structural || 0, 0)} kN/m per pile`],
      ['Combined vertical k_tot', `${fmtNum(stiffnesses.k_pile_vertical || 0, 0)} kN/m per pile`],
      ['Slab subgrade k_cap', `${fmtNum(stiffnesses.k_cap || 0, 0)} kN/m`],
      ['Pile lateral', `${fmtNum(stiffnesses.k_lateral_pile || 0, 0)} kN/m per pile`],
      ['Slab base friction', `${fmtNum(stiffnesses.k_lateral_slab_friction || 0, 0)} kN/m`],
      ['Slab passive pressure', `${fmtNum(stiffnesses.k_lateral_slab_passive || 0, 0)} kN/m`],
      ['Rotational k_rot', `${fmtNum(stiffnesses.k_rotational || 0, 0)} kNm/rad`],
    ]),
    { allowBreak: true },
  );

  const share = stab.load_distribution;
  if (share) {
    const shareRows = [
      ['Total vertical load', `${fmtNum(share.total_load, 0)} kN`],
      ['Pile load', `${fmtNum(share.pile_load, 0)} kN (${((Number(share.pile_fraction) || 0) * 100).toFixed(1)}%)`],
      ['Soil bearing', `${fmtNum(share.soil_load, 0)} kN (${((Number(share.soil_fraction) || 0) * 100).toFixed(1)}%)`],
    ];
    if (share.shear_x && share.shear_y) {
      shareRows.push(['Shear applied X / Y', `${fmtNum(share.shear_x.total_applied, 0)} / ${fmtNum(share.shear_y.total_applied, 0)} kN`]);
      shareRows.push(['Shear to piles X / Y', `${fmtNum(share.shear_x.pile, 0)} / ${fmtNum(share.shear_y.pile, 0)} kN`]);
      shareRows.push(['Soil friction X / Y', `${fmtNum(share.shear_x.soil_friction, 0)} / ${fmtNum(share.shear_y.soil_friction, 0)} kN`]);
      shareRows.push(['Passive pressure X / Y', `${fmtNum(share.shear_x.soil_passive, 0)} / ${fmtNum(share.shear_y.soil_passive, 0)} kN`]);
    }
    body += printSectionHtml('Load distribution', kvTable(shareRows));
  }

  if (stab.overturning) {
    const ot = stab.overturning;
    body += printSectionHtml(
      'Overturning',
      kvTable([
        ['Resisting moment', `${fmtNum(ot.resisting_moment, 1)} kNm`],
        ['Driving moment', `${fmtNum(ot.driving_moment, 1)} kNm`],
        ['Utilization η', escapeHtml(String(ot.utilization ?? '—'))],
        ['Requirement', 'η ≤ 1.0 (γ_M = 1.5 on resistance)'],
        ['Status', escapeHtml(ot.status || '—')],
      ]),
    );
  }

  if (stab.bearing) {
    const bearing = stab.bearing;
    body += printSectionHtml(
      'Soil bearing',
      kvTable([
        ['Average pressure', `${fmtNum(bearing.average_pressure, 1)} kPa`],
        ['Max pressure', `${fmtNum(bearing.max_pressure, 1)} kPa`],
        ['Design pressure q_d', `${fmtNum(bearing.allowable_pressure, 1)} kPa`],
        bearing.q_ult_kPa != null ? ['q_ult', `${fmtNum(bearing.q_ult_kPa, 1)} kPa`] : null,
        bearing.utilization_ec7 != null
          ? ['Bearing utilization', `${(Number(bearing.utilization_ec7) * 100).toFixed(1)}%`]
          : null,
        ['Eccentricity', `${bearing.eccentricity ?? '—'} m`],
        ['Status', escapeHtml(bearing.status || '—')],
      ]),
    );
  }

  const reinf = stab.reinforcement;
  if (reinf) {
    const reinfRows = [
      ['Bottom reinforcement As,bot', `${fmtNum(reinf.as_bot_mm2 || 0, 0)} mm²`],
      ['Top reinforcement As,top', `${fmtNum(reinf.as_top_mm2 || 0, 0)} mm²`],
      ['Minimum reinforcement As,min', `${fmtNum(reinf.as_min_mm2 || 0, 0)} mm²`],
      ['Hogging tension', `${fmtNum(reinf.tension_top_kN || 0, 1)} kN`],
    ];
    if (reinf.top_rebar_warning) {
      reinfRows.push([
        'Top reinforcement',
        `Hogging tension ${fmtNum(reinf.tension_top_kN, 1)} kN. Provide at least As,min = ${fmtNum(reinf.as_min_mm2, 0)} mm² at the top face.`,
      ]);
    }
    const ec2 = reinf.ec2_pile_cap;
    if (ec2) {
      if (ec2.column_punching?.skipped) {
        reinfRows.push(['Column punching', 'Not evaluated']);
      } else if (ec2.column_punching) {
        reinfRows.push([
          'Column punching',
          `${(Number(ec2.column_punching.utilization) * 100).toFixed(1)}% (V_Ed ${fmtNum(ec2.column_punching.V_Ed_kN, 1)} kN, V_Rd,c ${fmtNum(ec2.column_punching.V_Rd_kN, 1)} kN)`,
        ]);
      }
      if (ec2.pile_punching) {
        reinfRows.push(['Pile punching (max)', `${(Number(ec2.pile_punching.max_utilization) * 100).toFixed(1)}%`]);
      }
      if (ec2.bending_hogging) {
        reinfRows.push(['Hogging bending', `${(Number(ec2.bending_hogging.utilization) * 100).toFixed(1)}%`]);
      }
      if (ec2.strut_and_tie?.applicable) {
        reinfRows.push(['Strut-and-tie', `${(Number(ec2.strut_and_tie.strut_utilization) * 100).toFixed(1)}%`]);
      }
    }
    body += printSectionHtml('Slab reinforcement', kvTable(reinfRows), { allowBreak: true });
  }

  if (stab.pile_tension_warning) {
    body += printSectionHtml(
      'Pile tension',
      '<p>One or more piles are in tension.</p>',
    );
  }

  if (stab.soil_parameters) {
    const soil = stab.soil_parameters;
    body += printSectionHtml(
      'Soil parameters used',
      kvTable([
        ['c_u', `${fmtNum(soil.cu, 1)} kPa`],
        ['φ', `${fmtNum(soil.phi, 1)}°`],
        ['Type', escapeHtml(soil.type || '—')],
      ]),
    );
  }

  return buildPrintDocumentHtml({
    title: 'Piled foundation — analysis report',
    subtitle: sub,
    bodyHtml: body,
  });
}

export function openFoundationPrintReport(results, options) {
  openPrintHtmlDocument(buildFoundationPrintHtml(results, options));
}

/** --- Console --- */
export function buildConsolePrintHtml(calculation, results, { layoutSvgHtml = '', stmSvgHtml = '', generatedAt } = {}) {
  if (!calculation || !results) {
    return buildPrintDocumentHtml({ title: 'Console analysis', bodyHtml: '<p>No results.</p>' });
  }
  const sub = `${calculation.console_name || 'Console'} · ${generatedAt || new Date().toLocaleString()}`;
  const summary = results.console_design_checks?.overall_ok ? 'PASS' : 'FAIL';

  let body = printSectionHtml(
    'Summary of console analysis',
    `<table class="report kv-table"><tbody>
      <tr><td>Overall</td><td><strong>${summary}</strong></td></tr>
      <tr><td>Shear utilization</td><td class="num">${escapeHtml(String(results.console_utilization_shear ?? '—'))}%</td></tr>
      <tr><td>Moment utilization</td><td class="num">${escapeHtml(String(results.console_utilization_moment ?? '—'))}%</td></tr>
      <tr><td>Moment resistance</td><td class="num">${fmtNum(results.console_moment_capacity, 1)} kNm</td></tr>
      <tr><td>Shear resistance</td><td class="num">${fmtNum(results.console_shear_capacity, 1)} kN</td></tr>
    </tbody></table>`,
  );

  if (layoutSvgHtml) {
    body += printSectionHtml('Layout', `<div class="svg-wrap">${layoutSvgHtml}</div>`);
  }
  if (stmSvgHtml) {
    body += printSectionHtml('Strut-and-tie model', `<div class="svg-wrap">${stmSvgHtml}</div>`, { breakBefore: true });
  }

  const nodes = results.strut_tie_analysis?.nodes || {};
  const nodeRows = Object.entries(nodes)
    .map(
      ([id, n]) =>
        `<tr><td>N${escapeHtml(id)}</td><td>${escapeHtml(n.node_type || '')}</td><td class="num">${n.area != null ? n.area.toFixed(0) : '—'}</td><td class="num">${n.sigma_max != null ? n.sigma_max.toFixed(1) : '—'}</td><td class="num">${n.allowable_stress != null ? n.allowable_stress.toFixed(1) : '—'}</td><td>${n.capacity_ok ? 'OK' : 'FAIL'}</td></tr>`,
    )
    .join('');
  if (nodeRows) {
    body += printSectionHtml(
      'STM nodal stresses',
      `<table class="report allow-row-split"><thead><tr><th>Node</th><th>Type</th><th class="num">A (mm²)</th><th class="num">σ (MPa)</th><th class="num">Limit</th><th>OK</th></tr></thead><tbody>${nodeRows}</tbody></table>`,
      { allowBreak: true },
    );
  }

  const els = results.strut_tie_analysis?.elements || {};
  const elRows = Object.entries(els)
    .map(
      ([id, e]) =>
        `<tr><td>E${escapeHtml(id)}</td><td>${escapeHtml(e.element_type || '')}</td><td class="num">${e.force != null ? Math.abs(e.force).toFixed(1) : '—'}</td><td class="num">${e.capacity != null ? e.capacity.toFixed(1) : '—'}</td><td class="num">${e.utilization != null ? (e.utilization * 100).toFixed(1) : '—'}%</td><td>${e.capacity_ok ? 'OK' : 'FAIL'}</td></tr>`,
    )
    .join('');
  if (elRows) {
    body += printSectionHtml(
      'STM members',
      `<table class="report allow-row-split"><thead><tr><th>El</th><th>Type</th><th class="num">F (kN)</th><th class="num">${eurocodeToHtmlSubscripts('N_Rd')} (kN)</th><th class="num">Util.</th><th>OK</th></tr></thead><tbody>${elRows}</tbody></table>`,
      { allowBreak: true },
    );
  }

  const bp = results.bearing_pad_analysis;
  if (bp?.summary) {
    body += printSectionHtml(
      'Bearing pad (EN 1337-3)',
      `<p>${bp.summary.all_checks_pass ? 'All checks pass' : 'Design fail'}. Governing: ${escapeHtml(String(bp.summary.governing_check || '—'))}</p>
       <table class="report kv-table"><tbody>
         <tr><td>Pad width × depth</td><td class="num">${fmtNum(bp.configuration?.width, 0)} × ${fmtNum(bp.configuration?.depth, 0)} mm</td></tr>
         <tr><td>Max utilization</td><td class="num">${((bp.summary.max_utilization || 0) * 100).toFixed(1)}%</td></tr>
       </tbody></table>`,
    );
  }

  return buildPrintDocumentHtml({
    title: 'Console / corbel — analysis report',
    subtitle: sub,
    bodyHtml: body,
  });
}

export function openConsolePrintReport({ calculation, results, layoutSvgHtml, stmSvgHtml, generatedAt }) {
  openPrintHtmlDocument(
    buildConsolePrintHtml(calculation, results, { layoutSvgHtml, stmSvgHtml, generatedAt }),
  );
}

/** --- Composite column --- */
function normalizeCompositeResults(results) {
  if (!results) return { input_parameters: {}, analysisResults: {} };
  if (results.input_parameters && results.results) {
    return { input_parameters: results.input_parameters, analysisResults: results.results };
  }
  if (results.capacities) {
    return { input_parameters: results.input_parameters || {}, analysisResults: results };
  }
  return { input_parameters: {}, analysisResults: {} };
}

export function buildCompositePrintHtml(results, { generatedAt } = {}) {
  const { input_parameters, analysisResults } = normalizeCompositeResults(results);
  const capacities = analysisResults.capacities || {};
  const utilization = analysisResults.utilization || {};
  const detailedAmbient = analysisResults.detailed_ambient || {};
  const detailedFire = analysisResults.detailed_fire || {};
  const sectionProps = analysisResults.section_properties || {};
  const crossB64 = analysisResults.cross_section_data;
  const fireTempFieldB64 = analysisResults.plot_data_fire_temperature_field;

  const sub = [
    generatedAt || new Date().toLocaleString(),
    input_parameters?.section_name,
    input_parameters?.section_type,
  ]
    .filter(Boolean)
    .join(' · ');

  let body = printSectionHtml(
    'Case & loads',
    `<table class="report kv-table"><tbody>
      <tr><td>Length L</td><td class="num">${fmtNum(input_parameters?.comcol_length, 0)} mm</td></tr>
      <tr><td>${eurocodeToHtmlSubscripts('N_Ed')}</td><td class="num">${fmtNum(input_parameters?.N_Ed, 0)} kN</td></tr>
      <tr><td>${eurocodeToHtmlSubscripts('M_Ed,y')} / ${eurocodeToHtmlSubscripts('M_Ed,z')}</td><td class="num">${fmtNum(input_parameters?.M_Ed_y, 1)} / ${fmtNum(input_parameters?.M_Ed_z, 1)} kNm</td></tr>
    </tbody></table>`,
  );

  body += printSectionHtml(
    'ULS capacities',
    `<table class="report kv-table"><tbody>
      <tr><td>${eurocodeToHtmlSubscripts('N_pl,Rd')} (compression)</td><td class="num">${fmtNum(capacities.compression, 0)} kN (${((utilization.compression || 0) * 100).toFixed(1)}%)</td></tr>
      <tr><td>${eurocodeToHtmlSubscripts('N_b,Rd')} (buckling)</td><td class="num">${fmtNum(capacities.buckling, 0)} kN (${((utilization.buckling || 0) * 100).toFixed(1)}%)</td></tr>
      <tr><td>Tension ${eurocodeToHtmlSubscripts('N_pl,Rk')}</td><td class="num">${fmtNum(capacities.tension, 0)} kN</td></tr>
    </tbody></table>`,
  );

  if (detailedAmbient && Object.keys(detailedAmbient).length) {
    body += printSectionHtml(
      'Ambient M–N (second order)',
      `<table class="report kv-table"><tbody>
        <tr><td>${eurocodeToHtmlSubscripts('M_Rd,y')} / ${eurocodeToHtmlSubscripts('M_II,y')}</td><td class="num">${fmtNum(detailedAmbient.M_Rd_y / 1e6, 2)} / ${fmtNum(detailedAmbient.M_II_y / 1e6, 2)} kNm</td></tr>
        <tr><td>${eurocodeToHtmlSubscripts('M_Rd,z')} / ${eurocodeToHtmlSubscripts('M_II,z')}</td><td class="num">${fmtNum(detailedAmbient.M_Rd_z / 1e6, 2)} / ${fmtNum(detailedAmbient.M_II_z / 1e6, 2)} kNm</td></tr>
        <tr><td>Biaxial utilization</td><td class="num">${((detailedAmbient.util_biaxial || 0) * 100).toFixed(1)}%</td></tr>
      </tbody></table>`,
    );
  }

  if (input_parameters?.fire_design_enabled && detailedFire && Object.keys(detailedFire).length) {
    const ratings = Object.keys(detailedFire);
    const head = `<tr><th>Rating</th><th class="num">${eurocodeToHtmlSubscripts('N_fi,Rd')} (kN)</th><th class="num">${eurocodeToHtmlSubscripts('N_fi,Ed')} (kN)</th><th class="num">Util. %</th><th>Pass</th></tr>`;
    const rrows = ratings
      .map((r) => {
        const x = detailedFire[r];
        const nFiEd = x.N_fi_Ed_kN != null ? x.N_fi_Ed_kN : (x.N_fi_Ed != null ? x.N_fi_Ed / 1000 : 0);
        return `<tr><td>R${escapeHtml(r)}</td><td class="num">${fmtNum((x.N_fi_Rd || 0) / 1000, 0)}</td><td class="num">${fmtNum(nFiEd, 0)}</td><td class="num">${((x.util_fire || 0) * 100).toFixed(1)}</td><td>${x.pass ? 'Yes' : 'No'}</td></tr>`;
      })
      .join('');
    body += printSectionHtml(
      'Fire design (EN 1994-1-2)',
      `<table class="report"><thead>${head}</thead><tbody>${rrows}</tbody></table>`,
      { breakBefore: true },
    );
  }

  body += printSectionHtml(
    'Section properties',
    `<table class="report kv-table"><tbody>
      <tr><td>Steel area ${eurocodeToHtmlSubscripts('A_a')}</td><td class="num">${fmtNum(sectionProps.area_steel, 0)} mm²</td></tr>
      <tr><td>Concrete ${eurocodeToHtmlSubscripts('A_c')}</td><td class="num">${fmtNum(sectionProps.area_concrete, 0)} mm²</td></tr>
      <tr><td>Rebar ${eurocodeToHtmlSubscripts('A_s')}</td><td class="num">${fmtNum(sectionProps.area_rebar, 0)} mm²</td></tr>
    </tbody></table>`,
  );

  if (crossB64) {
    body += imgSection('Cross-section', crossB64, 'Cross-section');
  }

  if (fireTempFieldB64) {
    body += imgSection(
      'Fire — 2D FEM temperature field',
      fireTempFieldB64,
      'Temperature field',
    );
  }

  body += `<p class="muted">Interaction charts are omitted from this report; open the app for full diagrams.</p>`;

  return buildPrintDocumentHtml({
    title: 'Composite column — analysis report',
    subtitle: sub,
    bodyHtml: body,
  });
}

export function openCompositePrintReport(results, options) {
  openPrintHtmlDocument(buildCompositePrintHtml(results, options));
}
